// English source strings for the wave-2 features (configuration panels, execution control,
// work tree, chat, runs and observability). Merged into `en`; placeholders use {name}.
export const enWave2 = {
  "common.actions": "Actions",
  "common.edit": "Edit",
  "common.yes": "Yes",
  "common.no": "No",

  // structured refusals
  "err.blockedExternal": "Blocked — external integration not enabled",
  "err.blockedExternalBody": "Integration not enabled — requires credentials and explicit approval. Nothing was attempted on the server.",
  "err.policyDenied": "Refused by policy",
  "err.realProviderBody": "Real provider calls require explicit per-integration approval. No request was sent to the provider and no token was used.",
  "err.versionConflict": "Changed by someone else",
  "err.impediments": "Not possible yet — impediments",
  "err.validation": "Some fields are not valid",
  "err.refused": "The request was refused",
  "err.cause": "Cause: {cause}",
  "err.currentVersion": "Current version on the server: {n}. Reload and re-apply your change.",
  "err.stepNotRun": "not run",
  "err.reload": "Reload",

  // configuration shared
  "config.adminOnly": "Only administrators change this configuration; you can read it.",
  "config.version": "Version",
  "config.globalScope": "global",
  "config.projectScope": "project",
  "secret.missing": "Not set",
  "secret.active": "Stored",
  "secret.revoked": "Revoked",
  "state.draft": "Draft",
  "state.validated": "Validated",
  "state.active": "Active",
  "state.disabled": "Disabled",
  "state.auth_failed": "Authentication failed",
  "state.unverified": "Unverified",
  "state.verified": "Verified",
  "state.invalid": "Invalid",
  "state.running": "Running",
  "state.draining": "Draining",
  "state.degraded": "Degraded",
  "state.stopped": "Stopped",
  "state.crash_loop": "Crash loop",
  "state.healthy": "Healthy",
  "state.unavailable": "Unavailable",
  "state.unknown": "Unknown",
  "origin.value.configured": "configured",
  "origin.value.reported": "reported",
  "origin.value.estimated": "estimated",
  "origin.value.stale": "stale",
  "origin.value.unknown": "unknown",
  "origin.value.global": "inherited (global)",
  "origin.value.project": "project override",
  "origin.value.task": "task override",
  "origin.value.unset": "not set",
  "adapter.omniroute": "OmniRoute",
  "adapter.openrouter": "OpenRouter (direct)",
  "adapter.nvidia_nim": "NVIDIA NIM (direct)",
  "adapter.jev": "Jev",
  "adapter.openai_responses": "OpenAI Responses",
  "adapter.openai_chat": "OpenAI Chat Completions",
  "adapter.integration_module": "Integration (AI module)",
  "authMode.bearer": "Bearer token",
  "authMode.header": "API key header",
  "authMode.none": "No authentication",
  "cap.streaming": "Streaming",
  "cap.tool_calling": "Tool calling",
  "cap.multi_turn_tools": "Multi-turn tools",
  "cap.json_schema": "Strict JSON",
  "cap.images": "Images",
  "cap.prompt_cache": "Prompt cache",
  "cap.reasoning_summary": "Reasoning summary",
  "cap.reasoning_effort": "Reasoning effort",
  "capState.declared": "declared",
  "capState.verified": "verified",
  "capState.failed": "failed",
  "capState.unknown": "unknown",
  "testStatus.passed": "Passed",
  "testStatus.failed": "Failed",
  "testStatus.skipped": "Skipped",
  "testStatus.unknown": "Unknown",
  "targetKind.chat": "Chat",
  "targetKind.embedding": "Embedding",
  "targetKind.rerank": "Rerank",
  "targetKind.decision": "Decision",
  "agentRole.maestro": "Maestro",
  "agentRole.assistant": "Assistant",
  "agentRole.planner": "Planner",
  "agentRole.classifier": "Classifier (Jev)",
  "agentRole.researcher": "Researcher",
  "agentRole.builder": "Builder",
  "agentRole.tester": "Tester",
  "agentRole.critic": "Critic",
  "agentRole.integrator": "Integrator",
  "agentRole.compactor": "Compactor",
  "agentRole.coach": "Coach (agent trainer)",
  "reviewer.maestro": "Maestro",
  "reviewer.critic": "Critic",
  "reviewer.none": "No reviewer",
  "errorClass.rate_limited": "rate limited",
  "errorClass.credit_exhausted": "credit exhausted",
  "errorClass.unavailable": "unavailable",
  "errorClass.model_not_found": "model not found",
  "errorClass.context_exceeded": "context exceeded",
  "trigger.capability_missing": "Missing capability",
  "trigger.irreducible_context": "Irreducible context",
  "trigger.reproducible_quality_failure": "Reproducible quality failure",
  "trigger.risk_class": "Risk class",
  "trigger.material_stagnation": "Material stagnation",
  "window.day": "Day",
  "window.week": "Week",
  "window.month": "Month",
  "gauntletMode.conventional": "Conventional (single review)",
  "gauntletMode.gauntlet": "Gauntlet (builder ↔ critic rounds)",
  "gauntletReq.contract_change": "Contract changes",
  "gauntletReq.data_migration": "Data migrations",
  "gauntletReq.authentication": "Authentication",
  "gauntletReq.high_risk": "High-risk tasks",

  // tests
  "tests.consumesTokens": "This test sends a real request that consumes tokens. Set the maximum you accept to spend on it.",
  "tests.maxCost": "Maximum cost for this test (cents)",
  "tests.maxCostHint": "Required. The test is refused beyond this budget.",
  "tests.maxCostRequired": "Enter a maximum cost greater than zero.",
  "tests.running": "Test running…",
  "tests.failedToRun": "The test could not run",
  "tests.fixtureOnly": "Fixture only — not homologated",
  "tests.fixtureOnlyHint": "The result came from a test fixture, not from the real provider. It does not approve the model for production work.",
  "tests.kind": "Test",
  "tests.result": "Result",
  "tests.latency": "Latency",
  "tests.usedTokens": "Used tokens",
  "tests.when": "When",

  // providers
  "settings.tab.routing": "Routing & Maestro",
  "providers.new": "New connection",
  "providers.export": "Export (without secrets)",
  "providers.exportHint": "The export carries names, endpoints and references only; keys are never exported and imported connections must be revalidated.",
  "providers.emptyTitle": "No provider connections yet",
  "providers.emptyBody": "Add OmniRoute, direct OpenRouter, direct NVIDIA NIM or the approved Jev endpoint. Nothing is preselected.",
  "providers.noPool": "No shared quota pool",
  "providers.baseUrl": "Inference base URL",
  "providers.baseUrlHint": "The API base URL (for example …/v1), never a dashboard address.",
  "providers.authMode": "Authentication",
  "providers.quotaPool": "Quota pool",
  "providers.quotaPoolHint": "Connections that reach the same account share one pool so capacity is never counted twice.",
  "providers.allowedKinds": "Model kinds this connection may serve",
  "providers.allowedKindsHint": "Models of other kinds cannot be registered on this connection and routing never sends them here. Example: limit OpenRouter to Embedding and Rerank so chat models come from OmniRoute.",
  "providers.allowedKindsRequired": "Choose at least one model kind.",
  "providers.serves": "Serves",
  "providers.key": "API key",
  "providers.noKeyNeeded": "No key needed for this authentication mode",
  "providers.addKey": "Add key",
  "providers.replaceKey": "Replace key",
  "providers.revokeKey": "Revoke key",
  "providers.enable": "Return to draft",
  "providers.disable": "Disable",
  "providers.saved": "Connection saved.",
  "providers.tests": "Tests",
  "providers.testsHint": "Connection and catalog tests use no tokens; minimal inference does. A catalog entry is not proof of permission or capability.",
  "providers.testConnection": "Test connection",
  "providers.discoverCatalog": "Discover catalog",
  "providers.testInference": "Test minimal inference",
  "providers.inferenceTarget": "Model target to test",
  "providers.chooseTarget": "Choose a model target…",
  "providers.noTargets": "Register a model target for this connection first (Models & combos).",
  "providers.runInference": "Run inference test",
  "providers.catalogUnavailable": "Catalog unavailable",
  "providers.catalogUnavailableBody": "This connection does not offer a catalog. Register exact model IDs manually.",
  "providers.catalogNote": "Catalog entries are not proof of permission or capability; register exact IDs and probe them.",
  "providers.catalogEmpty": "The catalog returned no models.",
  "providers.notRegistered": "not registered",
  "providers.recentTests": "Recent tests",
  "providers.noTests": "No test recorded yet.",
  "providers.editTitle": "Edit “{name}”",
  "providers.dialogBody": "Changes use optimistic versions: if someone saved in the meantime, you will be asked to reload.",
  "providers.name": "Name",
  "providers.adapter": "Adapter",
  "providers.editNote": "Changing the endpoint or authentication requires testing again before use.",
  "providers.keyAfterCreate": "Add the key after creating the connection; it is stored write-only.",
  "providers.addKeyTitle": "Add key — {name}",
  "providers.replaceKeyTitle": "Replace key — {name}",
  "providers.keyDialogBody": "The key is sent once, stored in the vault and never shown again. Only its state and a masked identifier are displayed.",
  "providers.currentKey": "Current key:",
  "providers.apiKey": "API key",
  "providers.keyRetestHint": "A new key returns the connection to draft until a test passes.",
  "providers.storeKey": "Store key",
  "providers.keyStored": "Key stored. Test the connection before using it.",
  "providers.revokeTitle": "Revoke the key of “{name}”?",
  "providers.revokeBody": "Revocation takes effect immediately: new tests and calls are refused. Rolling back a configuration never restores a revoked key.",
  "providers.keyRevoked": "Key revoked.",
  "providers.moduleInstance": "AI module account",
  "providers.moduleInstanceHint": "Active AI provider accounts from Settings → Integrations. The key stays in the account; this connection keeps routing, budget and quota.",
  "providers.chooseModuleInstance": "Choose an account…",
  "providers.noModuleInstances": "No active AI provider account yet. Connect and activate one in Settings → Integrations.",
  "providers.moduleKey": "Kept in the module account (Integrations)",
  "providers.moduleInstanceUnavailable": "Account not available",

  // models
  "models.new": "New model target",
  "models.needConnection": "Create a provider connection first.",
  "models.exactIdHint": "External IDs are used exactly as entered — never mapped by similarity or inferred from a commercial name.",
  "models.emptyTitle": "No model targets yet",
  "models.emptyBody": "Register exact model IDs or combos per connection. Manual IDs stay unverified until an inference probe passes.",
  "models.renamedTitle": "Renamed “{from}” → “{to}”",
  "models.renamedBody": "The internal ID, links and history are kept. Verification was reset; test again before activating. Affected configuration:",
  "models.noDependents": "No agent profile or routing configuration uses this target.",
  "models.dependentProfile": "Agent profile “{name}” ({role})",
  "models.dependentConfig": "Routing configuration v{version} ({state}, {scope})",
  "models.strong": "Strong",
  "models.externalId": "External model ID",
  "models.connection": "Connection",
  "models.kindProtocol": "Type · protocol",
  "models.contextOutput": "Context / max output",
  "models.unknownValue": "unknown",
  "models.free": "Free of charge",
  "models.freeUnknown": "Unknown (never assumed free)",
  "models.freeYes": "Yes",
  "models.freeNo": "No",
  "models.freeHint": "Unknown is kept as unknown; it is never treated as free.",
  "models.pricing": "Pricing",
  "models.pricingValue": "{currency} {input} in / {output} out per million tokens · valid from {from} · {source}",
  "models.pricingUnknown": "Unknown",
  "models.verifiedAt": "Verified",
  "models.neverVerified": "Never verified",
  "models.capabilities": "Capabilities",
  "models.capabilitiesLegend": "Capabilities: declared (not proven) vs verified by a probe",
  "models.noCapabilities": "No capability declared or verified.",
  "models.probeCapabilities": "Probe capabilities",
  "models.conformance": "Backend conformance",
  "models.runProbe": "Run probe",
  "models.historyAndDependents": "History, probes and dependents",
  "models.dependents": "Used by",
  "models.versions": "Versions",
  "models.probes": "Probes",
  "models.noProbes": "No probe recorded yet.",
  "models.returnedModel": "returned {model}",
  "models.editTitle": "Edit “{name}”",
  "models.dialogBody": "Enter the exact external ID. Unknown values stay unknown; conservative limits apply until they are known.",
  "models.renameHint": "Changing the external ID keeps the internal ID and history, resets verification and lists the affected configuration.",
  "models.externalIdHint": "Exact ID or alias as the provider expects it.",
  "models.displayName": "Display name",
  "models.alias": "Internal alias",
  "models.aliasHint": "Stable internal name such as free-coder-primary (lowercase, digits, hyphens).",
  "models.kind": "Type",
  "models.kindNotAllowedOption": "{kind} — not served by this connection",
  "models.kindRestricted": "This connection serves only: {kinds}. Change it in Settings → Providers & keys.",
  "models.kindNotServed": "This type is not served by the connection (it serves only: {kinds}). Routing treats this model as unavailable; choose a served type.",
  "models.protocol": "Protocol",
  "models.contextWindow": "Context window (tokens, declared)",
  "models.maxOutput": "Max output (tokens, declared)",
  "models.unknownAllowed": "Leave empty when unknown.",
  "models.declaredSource": "Source: the operator's entry in this form, not verified by a probe. Leave empty when unknown.",
  "models.declaredTag": "declared · source: operator entry",
  "models.declaredCapabilities": "Declared capabilities",
  "models.declaredHint": "A declaration is not proof; only passed probes mark a capability as verified.",
  "models.strongFlag": "Strong target (administrators only)",
  "models.strongHint": "Only strong targets may act as Maestro or Maestro substitute. This is an explicit administrator decision, never inferred from a model name.",
  "models.pricingKnown": "Pricing is known",
  "models.currency": "Currency (ISO)",
  "models.inputPerMtok": "Input per million tokens",
  "models.outputPerMtok": "Output per million tokens",
  "models.validFrom": "Valid from",
  "models.priceSource": "Source",
  "models.editResetsVerification": "Changing the external ID resets verification until a new probe passes.",
  "models.invalidNumber": "Check the numeric fields.",
  "models.pricingIncomplete": "Pricing needs a 3-letter currency, a validity date and its source.",
  "models.saved": "Model target saved.",

  // agents
  "agents.modelVsExecutor": "A model is not an executor: changing it does not install a CLI, grant tools or create a runner. “No model” marks a deterministic executor.",
  "agents.matrix": "Agent profiles per role",
  "agents.role": "Role",
  "agents.profiles": "Agent profiles",
  "agents.maestroMandatory": "Mandatory for delivery acceptance.",
  "agents.noProfiles": "No profile for this role.",
  "agents.model": "Main model",
  "agents.noModel": "No model (deterministic executor)",
  "agents.fallbacks": "Fallbacks",
  "agents.reviewer": "Reviewer",
  "agents.tools": "Tools",
  "agents.concurrency": "Concurrency",
  "agents.editProfile": "Edit {name}",
  "agents.add": "Add profile",
  "agents.addFor": "Add a {role} profile",
  "agents.appliesToNew": "Changes apply to new attempts only; running attempts keep their pinned route.",
  "agents.newTitle": "New {role} profile",
  "agents.dialogBody": "Several profiles may exist for the same role with different models; routing decides which one takes a task.",
  "agents.name": "Name",
  "agents.chooseModel": "Choose a model target…",
  "agents.modelHint": "Unverified targets may be selected, but routing validation lists them as impediments.",
  "agents.toolsHint": "Comma-separated tool names allowed to this profile.",
  "agents.contextLimit": "Context limit (tokens)",
  "agents.outputLimit": "Output limit (tokens)",
  "agents.maxCostPerAttempt": "Max cost per attempt (cents)",
  "agents.maxCalls": "Max calls per attempt",
  "agents.fallbacksHint": "Ordered list; a fallback is chosen before an attempt or after a checkpoint, never mid-stream.",
  "agents.moveUp": "Move fallback {n} up",
  "agents.moveDown": "Move fallback {n} down",
  "agents.addFallback": "Add a fallback",
  "agents.instructions": "Instructions",
  "agents.disableProfile": "Disable this profile",
  "agents.nameAndModelRequired": "Give the profile a name and choose a model (or “no model”).",
  "agents.saved": "Agent profile saved.",

  // quotas
  "quotas.new": "New quota pool",
  "quotas.dataAsOf": "Data as of {time}",
  "quotas.emptyTitle": "No quota pools yet",
  "quotas.emptyBody": "Create a pool per provider account and link the connections and targets that share it.",
  "quotas.confidence": "confidence {pct}%",
  "quotas.usedOf": "{used} of {limit} requests in the current window",
  "quotas.conservative": "conservative limit applied",
  "quotas.noRpmLimit": "{used} requests in the current window · no RPM limit known",
  "quotas.rpm": "Requests per minute",
  "quotas.effective": "effective {n}",
  "quotas.tpm": "Tokens per minute",
  "quotas.concurrency": "Concurrency",
  "quotas.budget": "Spend ceiling",
  "quotas.reserved": "Reserved",
  "quotas.inFlight": "In flight",
  "quotas.waiting": "Tasks waiting",
  "quotas.resetIn": "Window resets in",
  "quotas.resetUnknown": "unknown",
  "quotas.nextSlot": "Next free slot in",
  "quotas.reported": "Provider reported {requests} requests and {tokens} tokens remaining ({when})",
  "quotas.noReport": "No quota reported by the provider; a missing balance is unknown, not zero.",
  "quotas.sharedBy": "Shared by",
  "quotas.noLinks": "No connection or target uses this pool yet.",
  "quotas.sharedNote": "{n} connections (gateway and direct) share this pool: one limit, never doubled.",
  "quotas.editTitle": "Edit pool “{name}”",
  "quotas.dialogBody": "Limits apply to every connection and target linked to the pool.",
  "quotas.name": "Name",
  "quotas.emptyIsUnknown": "Leave empty when unknown.",
  "quotas.budgetCents": "Spend ceiling (cents)",
  "quotas.safetyMargin": "Safety margin",
  "quotas.safetyMarginHint": "Fraction of the limit kept free (0 – 0.5).",
  "quotas.origin": "Origin of the limit",
  "quotas.originHint": "Distinguishes a local limit, a reported quota, an estimate and a stale value.",
  "quotas.saved": "Quota pool saved.",

  // routing
  "routing.intro": "Drafts are validated against the current providers, targets and profiles; only a draft without impediments can be activated. Activation affects new attempts only.",
  "routing.newDraft": "New draft",
  "routing.newDraftFromActive": "New draft from active v{n}",
  "routing.draftSaved": "Draft v{n} saved and validated.",
  "routing.currentAndDrafts": "Active configuration and pending drafts",
  "routing.noConfigs": "No routing configuration yet.",
  "routing.historyHint": "Every version, with its diff and rollback, is in",
  "routing.roles": "Roles",
  "routing.rolesHint": "Choose the default agent profile per role. Roles left unconfigured are listed as impediments when they are required.",
  "routing.defaultAgent": "Default agent",
  "routing.defaultAgentFor": "Default agent for {role}",
  "routing.fallbacksAndCaps": "Required capabilities and fallbacks",
  "routing.notConfigured": "Not configured",
  "routing.allowedAgents": "Other agents allowed to take tasks",
  "routing.routeSummary": "{caps} required capabilities · {fallbacks} fallbacks",
  "routing.requiredCaps": "Required capabilities",
  "routing.addFallbackFor": "Fallback model for {role}",
  "routing.fallbackOn": "Use it on",
  "routing.addFallback": "Add fallback",
  "routing.maestro": "Maestro",
  "routing.maestroHint": "The Maestro accepts deliveries and cannot be disabled. Only targets marked strong by an administrator may act as Maestro or substitute.",
  "routing.maestroPrimary": "Primary Maestro",
  "routing.maestroNone": "Not selected",
  "routing.notStrong": "not strong",
  "routing.noStrongTargets": "No target is marked strong yet (Models & combos).",
  "routing.substitutes": "Strong substitutes",
  "routing.substitutesList": "substitutes: {list}",
  "routing.freeFirst": "Free first",
  "routing.freeFirstHint": "Paid or strong implementation routes are used only after one of these versioned triggers.",
  "routing.freeFirstEnabled": "Prefer free routes",
  "routing.escalationTriggers": "Escalation triggers",
  "routing.budgets": "Budgets",
  "routing.softLimit": "Soft limit (cents)",
  "routing.hardLimit": "Hard limit (cents)",
  "routing.window": "Budget window",
  "routing.gauntlet": "Gauntlet loop",
  "routing.gauntletHint": "Builder and critic alternate until the evaluation contract is met, the round limit is reached or progress stagnates.",
  "routing.gauntletMode": "Mode",
  "routing.maxRounds": "Maximum rounds",
  "routing.stagnationRounds": "Stagnation after rounds without progress",
  "routing.stagnationHint": "Rounds without material progress before the loop stops and escalates.",
  "routing.gauntletRequiredFor": "Required for",
  "routing.saveDraft": "Save draft and validate",
  "routing.saveDraftHint": "Saving creates a new draft version; nothing is activated.",
  "routing.notValidated": "Not validated yet",
  "routing.valid": "No impediments",
  "routing.impedimentCount": "{n} impediments",
  "routing.versionN": "Version {n}",
  "routing.activatedAt": "activated {when}",
  "routing.author": "by {who}",
  "routing.validate": "Validate",
  "routing.activate": "Activate",
  "routing.rollbackTo": "Roll back to v{n}",
  "routing.activated": "Version {n} is now active.",
  "routing.noChanges": "No change since v{n}.",
  "routing.changedSince": "Changed since v{n}: {list}",
  "routing.firstVersion": "First version.",

  "effective.title": "Effective configuration per project",
  "effective.intro": "Resolved global → project with the origin of each value: agent → role → model/API → tools → quota pool → fallback → reviewer/Maestro.",
  "effective.chooseProject": "Project",
  "effective.pickProject": "Choose a project…",
  "effective.openProjectOverrides": "Open this project's overrides",
  "effective.ready": "Ready to execute",
  "effective.notReady": "Not ready",
  "effective.versions": "Global v{global} · project v{project}",
  "effective.caption": "Effective configuration: agent, role, model and API, tools, quota pool, fallbacks, reviewer and origin",
  "effective.agent": "Agent",
  "effective.modelApi": "Model / API",
  "effective.origin": "Origin",
  "effective.toolsDeclared": "Declared, not enforced by the engine: {list}",

  "history.rollbackNote": "Rollback activates an earlier version again after revalidating it; it never restores a revoked key.",
  "gauntlet.activeFrom": "Values of the active global configuration (v{n}).",
  "gauntlet.noActive": "No global configuration is active; defaults apply once one is activated.",
  "gauntlet.builderCritic": "Builder and critic come from the agent profiles of those roles; round results appear in each task's work tree.",
  "gauntlet.editInRouting": "Edit in Routing & Maestro",

  // infrastructure
  "infra.adminOnlyTitle": "Infrastructure is managed by administrators",
  "infra.adminOnlyBody": "Servers, SSH credentials and Coolify connections are visible to administrators only.",
  "infra.blockedNote": "Registration and policy are stored now. SSH and Coolify network steps are not enabled in this release: tests return “blocked” and nothing is attempted on the server.",
  "infra.servers": "Registered servers",
  "infra.noServers": "No server registered yet.",
  "infra.purpose.development": "Development",
  "infra.purpose.testing": "Testing",
  "infra.address": "Address",
  "infra.expectedFingerprint": "Expected host key fingerprint",
  "infra.notDeclared": "Not declared",
  "infra.policy": "What the autonomy preset allows on this server",
  "infra.policyBound": "Bound to this server only; other servers, production, cloud accounts and billing never inherit it.",
  "infra.grants": "Allowed",
  "infra.excluded": "Never allowed",
  "infra.disable": "Disable server",
  "infra.resultTitle": "Result of “{action}”",
  "infra.unexpectedSuccess": "The server accepted the request.",
  "infra.noWebLogin": "No web login configured.",
  "infra.coolifyCapabilitiesUnknown": "Capabilities unknown until the integration is enabled.",
  "infra.registerServer": "Register a server",
  "infra.declaredOs": "Declared system",
  "infra.declaredArch": "Declared architecture",
  "infra.fingerprintHint": "OpenSSH SHA256 format. A different key on first validation blocks the connection for identity review.",
  "infra.observedHint": "Recorded by the first real validation.",
  "infra.operation": "Workspace and autonomy",
  "infra.presetHint": "Applies to this server only and can be revoked by disabling the server.",
  "infra.saveServer": "Register server",
  "infra.saveServerHint": "Secrets are sent once and stored in the vault; the form is cleared after saving.",
  "infra.serverSaved": "Server registered. Secrets are stored; only their state is shown.",
  "infra.rootElevation": "The root user uses the root elevation mode.",
  "infra.fingerprintFormat": "Use the OpenSSH format SHA256:<base64>.",
  "infra.absolutePath": "Use an absolute path (starting with /).",
  "infra.portRange": "Use a port between 1 and 65535.",
  "infra.registerCoolify": "Register a Coolify connection",
  "infra.server": "Server",
  "infra.chooseServer": "Choose a server…",
  "infra.registerServerFirst": "Register a server first.",
  "infra.saveCoolify": "Register Coolify connection",
  "infra.coolifySaved": "Coolify connection registered.",
  "infra.coolifyRequired": "Choose a server and enter the Coolify base URL.",
  "infra.webUserRequired": "A web password requires the web user.",
  "infra.disableTitle": "Disable “{name}”?",
  "infra.disableBody": "Disabling revokes the server's authorization for new actions immediately. It can be re-registered later.",
  "infra.disabled": "Server disabled.",
  "elevation.root": "Root",
  "elevation.sudo_nopasswd": "Passwordless sudo",
  "elevation.sudo_password": "Sudo with a separate password",

  // project execution
  "project.drain": "Drain",
  "project.stop": "Stop",
  "project.startHint": "Starts ready tasks within the policy.",
  "project.pauseHint": "Pauses running work at the next safe point.",
  "project.resumeHint": "Continues paused or draining work.",
  "project.drainHint": "Finishes running attempts and starts nothing new.",
  "project.stopHint": "Cancels running executions; history is kept.",
  "project.drainConfirmTitle": "Drain this project?",
  "project.stopConfirmTitle": "Stop this project?",
  "project.execution.drainRequested": "Drain requested.",
  "project.execution.stopRequested": "Stop requested.",
  "project.moreExecution": "More",
  "project.validateNow": "Validate now",
  "project.validateAgain": "Validate again",
  "project.validating": "Validating…",
  "project.starting": "Starting…",
  "project.startBlockedToast": "Could not start: {n} items to fix",
  "project.startNotPossible": "Could not start: the readiness check did not finish.",
  "project.validateStillRunning": "The readiness check is still running; try again in a moment.",
  "project.validateDone": "Readiness check finished.",
  "project.validateFailed": "The readiness check did not finish: {message}",
  "project.readinessBadge.ready": "Ready",
  "project.readinessBadge.readyChecked": "Ready · checked {when}",
  "project.readinessBadge.notReadyChecked": "Not ready · {n} impediments · checked {when}",
  "project.readinessBadge.notReadyChecked.one": "Not ready · 1 impediment · checked {when}",
  "project.readinessBadge.notReadyNever": "Not ready · {n} impediments · never checked",
  "project.readinessBadge.notReadyNever.one": "Not ready · 1 impediment · never checked",
  "project.readinessBadge.openAssistant": "{status} — open the assistant",
  "readiness.status.passed": "Passed",
  "readiness.status.failed": "Failed",
  "readiness.status.unknown": "Unknown",
  "readiness.status.not_applicable": "Not applicable",
  "readiness.check.repository": "Repository",
  "readiness.check.base_branch": "Base branch",
  "readiness.check.workspace_authorized": "Workspace authorized",
  "readiness.check.runner_healthy": "Runner healthy",
  "readiness.check.executor_available": "Executor available",
  "readiness.check.test_command": "Test command",
  "readiness.check.eligible_route": "Eligible model route",
  "project.engineErrors": "State changed, but the engine reported errors",

  // project routing
  "projectRouting.title": "Routing for this project",
  "projectRouting.intro": "Values are inherited from the global configuration unless this project overrides them. Overrides cannot widen permissions or limits.",
  "projectRouting.editOverride": "Edit overrides",
  "projectRouting.restoreInheritance": "Restore inheritance",
  "projectRouting.overrideTitle": "Project overrides (draft)",
  "projectRouting.overrideHint": "“Inherit” keeps the global value. Saving creates a draft and shows the server validation before anything is activated.",
  "projectRouting.inherit": "Inherit from global",
  "projectRouting.hardLimitHint": "Leave empty to inherit. A project cannot raise the global ceiling.",
  "projectRouting.preview": "Save draft and preview",
  "projectRouting.previewTitle": "Preview of draft v{n}",
  "projectRouting.previewNote": "The server validated this draft and resolved the table as if it were active (read-only preview). Nothing changes until you activate it.",
  "projectRouting.changes": "Changes proposed by the draft",
  "projectRouting.current": "Current (effective)",
  "projectRouting.proposed": "After activation",
  "projectRouting.inheritedFromGlobal": "Inherited from global",
  "projectRouting.noRoleChanges": "The draft changes no role assignment.",
  "projectRouting.activate": "Activate draft",
  "projectRouting.fixImpediments": "Resolve the impediments before activating.",
  "projectRouting.versions": "Project versions ({n})",
  "projectRouting.noVersions": "This project has no override yet.",
  "maestro.title": "Maestro for this project",
  "maestro.primary": "Maestro",
  "maestro.epoch": "Supervision epoch",
  "maestro.noActiveGoal": "No active goal",
  "maestro.readiness": "Readiness",
  "maestro.lastReview": "Last review",
  "maestro.override": "Override Maestro",
  "maestro.overrideHint": "Switching the Maestro opens a new supervision epoch; past approvals are not reinterpreted.",
  "maestro.apply": "Apply Maestro",
  "maestro.saved": "Maestro updated.",
  "maestro.newEpoch": "New supervision epoch: {list}",

  // overview / work tree
  "overview.attemptsTree": "Attempts and steps",
  "overview.usageUnavailable": "Usage could not be read",
  "trace.loadFailed": "Could not load the work tree",
  "trace.noAttempts": "No attempt recorded yet.",
  "trace.attempts": "Attempts",
  "trace.attemptN": "Attempt {n}",
  "trace.agent": "agent {name}",
  "trace.effectiveModel": "model {model}",
  "trace.pinnedModel": "pinned {model} (effective not reported)",
  "trace.modelNotReported": "model not reported",
  "trace.rerunOf": "Re-run of attempt {n}",
  "trace.fixedBy": "Fixed by attempt {n}",
  "trace.divergence": "Requested {requested}, provider answered with {effective}",
  "trace.usageLoading": "Usage loading…",
  "trace.noCalls": "No model call recorded",
  "trace.running": "running",
  "trace.duration": "duration {value}",
  "trace.elapsed": "elapsed {value}",
  "trace.role": "role {role}",
  "trace.otherSteps": "Steps outside attempts",
  "trace.gauntletRound": "Gauntlet round {n} of {max}",
  "trace.gauntletCost": "cost {value}",
  "trace.gauntletCostPartial": "known cost {value} · {n} rounds with unknown cost",
  "trace.roundN": "Round {n}",
  "trace.builderCritic": "builder attempt {builder} · critic attempt {critic}",
  "trace.resolvedCriteria": "{n} criteria resolved",
  "trace.mainFinding": "Main finding: {text}",
  "trace.maestroReviews": "Maestro reviews",
  "trace.epoch": "epoch {n}",
  "trace.generatedAt": "Trace as of {time}",
  "trace.orphans": "{n} spans without a known parent",
  "usage.inputTokens": "Input {value} tokens",
  "usage.outputTokens": "Output {value} tokens",
  "usage.unknown": "unknown",
  "usage.partial": "{n} (known for {k} of {total} calls)",
  "usage.costTotal": "Cost {value}",
  "usage.costPartial": "Known cost {value} · total unknown ({n} calls without cost)",
  "usage.costUnknown": "Cost unknown ({n} calls without cost)",
  "usage.costUnknownShort": "cost unknown",
  "usage.estimatedPart": "{value} estimated",

  // chat
  "chat.scope": "Answers from this project's data",
  "chat.log": "Conversation",
  "chat.empty": "Ask about status, cost, pending or blocked work, or create a task. Answers come from the project data, with links.",
  "chat.modes": "Message type",
  "chat.mode.ask": "Ask",
  "chat.mode.task": "New task",
  "chat.mode.guidance": "Guidance",
  "chat.requestLabel": "Describe the new demand",
  "chat.requestPlaceholder": "Describe the demand; the text is kept verbatim…",
  "chat.taskTitle": "Task title",
  "chat.criteria": "Acceptance criteria",
  "chat.criteriaHint": "One per line.",
  "chat.guidanceTask": "Task",
  "chat.guidanceHint": "Guidance generates an event for the task; it never silently changes the criteria of a running attempt.",
  "chat.chooseTask": "Choose a task…",
  "chat.taskRequired": "Describe the demand (the first line becomes the title when none is given).",
  "chat.guidanceRequired": "Choose a task and write the guidance.",
  "chat.commandHint": "Commands follow the same permissions and policies as the panel; sending twice never duplicates.",
  "chat.stop": "Stop waiting for the reply",
  "chat.stopHint": "Stopping only stops waiting for this reply; it never cancels tasks.",
  "chat.historyUnavailable": "Earlier messages could not be loaded; a new conversation will start.",
  "chat.kind.requested": "Requested action",
  "chat.kind.inProgress": "Action in progress",
  "chat.kind.proven": "Result confirmed by the server",
  "chat.kind.answer": "Answer from project data",
  "chat.kind.explanation": "Explanation",
  "chat.thinking": "Reading project data…",
  "chat.cancelled": "Reply cancelled. Tasks are not affected.",
  "chat.cancelledCommand": "If the command already reached the server it may have been applied. Checking reuses the same request key, so nothing is duplicated.",
  "chat.checkOutcome": "Check the outcome",
  "chat.failed": "The message was not processed",
  "chat.fromHistory": "earlier message (links are not stored)",
  "chat.refs": "Related items",
  "chat.signalSent": "The running task was notified.",
  "chat.signalUnavailable": "Guidance recorded; the engine is unavailable, so it will be read at the next attempt.",
  "chat.dataAsOf": "Data as of {time} (event {cursor})",
  "chat.source": "source: {source}",
  "chat.cmd.createTask": "Create task “{title}”",
  "chat.cmd.guidance": "Add guidance to a task",
  "chat.cmd.importPrd": "Import PRD “{title}”",
  "chatRef.task": "Task",
  "chatRef.board": "Kanban",
  "chatRef.project": "Project",
  "chatRef.usage": "Usage and runs",
  "chatRef.versions": "Versions",
  "chatRef.prds": "PRDs",
  "chatRef.prd_version": "PRD version",
  "home.conversations": "Conversations",
  "home.newChat": "New chat",
  "home.noConversations": "Your conversations will appear here.",
  "home.moreConversations": "Older conversations",
  "home.latestConversations": "Latest conversations",
  "home.chatTitle": "What would you like to talk about?",
  "home.mode": "What do you want to do?",
  "home.mode.build": "Build",
  "home.mode.ask": "Chat",
  "home.askFallbackTitle": "Conversation",
  "home.askPrivate": "Private to you: other members never see this conversation. Answers come from the projects you can access.",

  // runs
  "runs.attempts": "Attempts",
  "runs.project": "Project",
  "runs.task": "Task",
  "runs.attempt": "Attempt",
  "runs.state": "State",
  "runs.started": "Started",
  "runs.duration": "Duration",
  "runs.noAttempts": "No attempt yet",
  "runs.traceFailed": "Trace unavailable",
  "runs.showTree": "Show tree",
  "runs.hideTree": "Hide tree",
  "runs.emptyTitle": "No runs yet",
  "runs.emptyBody": "Attempts appear once tasks leave the backlog and the execution engine runs them.",
  "runs.noProjectsTitle": "No projects yet",
  "runs.noProjectsBody": "Create a project to see its runs here.",

  // observability
  "obs.health": "Component health",
  "obs.noComponents": "The API did not report components.",
  "obs.component": "Component",
  "obs.state": "State",
  "obs.detail": "Detail",
  "obs.checkedAt": "Checked",
  "obs.runtime": "Runtime services",
  "reports.title": "Reports",
  "reports.bottlenecks": "Bottlenecks: last 15 days vs the 15 days before",
  "reports.groupBy": "Group by",
  "reports.group.step": "Step",
  "reports.group.model": "Model",
  "reports.group.role": "Role",
  "reports.group": "Group",
  "reports.before": "Before",
  "reports.after": "After",
  "reports.delta": "p50 change",
  "reports.notComparable": "not enough samples",
  "reports.qualityCost": "{succeeded}/{attempts} attempts succeeded ({success}) · tests passed {passed}/{gates} ({passRate}) · known cost {cost}, {unknown} call(s) without a known cost",
  "reports.noSpans": "No measured steps in these windows yet.",
  "reports.sameConditions": "Both windows used the same models and task types.",
  "reports.differentConditions": "The windows differ in models or task types: compare with care.",
  "reports.reviewModes": "Conventional review vs Gauntlet (last 30 days)",
  "reports.taskClass": "Task class",
  "reports.mode": "Mode",
  "reports.tasks": "Accepted / tasks",
  "reports.firstRound": "First-round acceptance",
  "reports.rounds": "Avg. rounds",
  "reports.rework": "Rework",
  "reports.costPerAccepted": "Cost per accepted",
  "reports.costUnknown": "unknown",
  "reports.conventional": "Conventional",
  "reports.noReviews": "No reviewed task in this window yet.",
  "obs.retention": "Retention",
  "obs.retentionDetailed": "Detailed logs are kept for {n} days.",
  "obs.retentionEnforcement": "The purge runs in the worker only after the operator enables it; deleted logs cannot be restored except from a backup. Usage, spans and the audit trail are never purged.",
  "obs.retentionNotEnforced": "{what}: {n} days (not enforced yet)",
  "obs.retentionChange": "Change the retention of detailed logs",
  "obs.retentionDaysLabel": "Days of detailed logs",
  "obs.retentionDaysRange": "Enter a whole number of days between 1 and 3650.",
  "obs.retentionRun": "{when}: {n} log lines older than {cutoff} removed",
  "obs.retentionNoRuns": "No purge has run yet.",
  "obs.retentionNoteTitle": "Older detail expired",
  "obs.alerts": "Alerts",
  "obs.alertsShowRecovered": "Show recovered",
  "obs.alertsNone": "No active alert: workers are beating and telemetry is up to date.",
  "obs.alertsNoneRecovered": "No recovered alert yet.",
  "obs.alertSeverity": "Severity",
  "obs.alertRule": "Alert",
  "obs.alertCause": "Cause",
  "obs.alertOpened": "Opened",
  "obs.alertPlatform": "platform",
  "obs.alertOrganization": "organization",
  "obs.alertOccurrences": "{n} occurrences",
  "obs.alertRecoveredAt": "recovered {when}",
  "obs.alertAcknowledge": "Acknowledge",
  "obs.healthCol": "Health",
  "alertRule.runner_heartbeat_lost": "Worker stopped responding",
  "alertRule.runner_crash_loop": "Worker in a crash loop",
  "alertRule.telemetry_delayed": "Telemetry delayed",
  "alertState.open": "Open",
  "alertState.acknowledged": "Acknowledged",
  "alertState.recovered": "Recovered",
  "severity.critical": "Critical",
  "severity.high": "High",
  "severity.medium": "Medium",
  "severity.low": "Low",
  "severity.info": "Info",
  "obs.noServices": "No runtime service has registered yet.",
  "obs.service": "Service",
  "obs.heartbeat": "Last heartbeat",
  "obs.noHeartbeat": "never",
  "obs.stale": "stale",
  "obs.version": "Version",
  "obs.rowVersion": "Row version",
  "obs.restarts": "Restarts",
  "obs.globalManaged": "Shared service — managed by the platform operator",
  "obs.actionsFor": "Request an action for {service}",
  "obs.request": "Request",
  "obs.confirmAction": "Request “{action}” for {service}?",
  "obs.confirmBody": "The request is recorded and applied by the worker. The panel shows it as requested until the service reports the change.",
  "obs.sendRequest": "Send request",
  "obs.requestedNotApplied": "“{action}” requested for {service} — not applied",
  "obs.pendingWorker": "Requested, not applied: applying it requires the worker function app.runtime_apply_action, which is pending.",
  "obs.operation": "Operation {id}",
  "obs.generatedAt": "Data as of {time}",
  "obs.logs": "Logs",
  "obs.exportCsv": "Export CSV",
  "obs.exportJson": "Export JSON",
  "obs.noProjects": "No projects yet",
  "obs.noProjectsBody": "Logs are kept per project.",
  "obs.filters": "Log filters",
  "obs.levels": "Levels",
  "obs.code": "Code",
  "obs.taskId": "Task ID",
  "obs.search": "Search in messages",
  "obs.apply": "Apply filters",
  "obs.redactedNote": "Secrets are redacted before logs leave the server. Exports use the same filters (up to 1000 rows).",
  "obs.noLogs": "No log matches these filters.",
  "obs.time": "Time",
  "obs.level": "Level",
  "obs.message": "Message",
  "obs.truncated": "truncated",
  "obs.openTrace": "Open trace",
  "obs.loadMore": "Load more",
  "obs.endOfLogs": "{n} entries — end of results.",
  "obs.traceTitle": "Task trace",
  "obs.traceFor": "Trace of task",
  "obs.traceHint": "Open a trace from a log entry or enter a task ID.",
  "obs.taskIdInvalid": "Enter a task ID (UUID).",
  "runtimeAction.drain": "Drain",
  "runtimeAction.resume": "Resume",
  "runtimeAction.restart": "Restart",
  "scope.global": "global",
  "scope.organization": "organization",
  "logLevel.debug": "Debug",
  "logLevel.info": "Info",
  "logLevel.warn": "Warning",
  "logLevel.error": "Error",
  "effective.configuration": "Configuration",
  "effective.activeConfig": "Active configuration",
  "effective.previewOf": "Preview of v{n} ({state})",
  "effective.previewHint": "A preview resolves a draft as if it were active; nothing is activated.",
  "effective.previewNotActive": "Preview only — this configuration is not active.",
  "infra.coolifyUpdated": "Coolify connection updated.",
  "infra.editBody": "Only changed fields are sent. Changing access (host, user, authentication, elevation or fingerprint) returns the server to draft until it is validated again.",
  "infra.editCoolify": "Edit Coolify connection",
  "infra.editTitle": "Edit “{name}”",
  "infra.noCoolify": "No Coolify connection for this server.",
  "infra.replace": "Replace",
  "infra.set": "Set",
  "infra.revoke": "Revoke",
  "infra.replaceSecret": "Replace {name}",
  "infra.revokeSecret": "Revoke {name}",
  "infra.replaceTitle": "Replace {name}",
  "infra.revokeTitle": "Revoke {name}?",
  "infra.replaceBody": "The new value is sent once and stored in the vault; only its masked state returns. The server must be validated again.",
  "infra.revokeBody": "Revocation takes effect immediately and the ciphertext is wiped; it is never restored by a rollback.",
  "infra.secretStored": "Secret stored. Only its masked state is shown.",
  "infra.secretRevoked": "Secret revoked.",
  "infra.serverUpdated": "Server updated.",
  "infra.switchSecretHint": "Required by the new configuration; credentials it no longer uses are revoked.",
  "infra.revalidationHint": "Secrets are rotated individually from the server card.",
  "infra.webUserChangeHint": "Changing the user revokes the stored web password.",
  "projectRouting.fullPreview": "Full effective table after activation",
  "projectRouting.previewFailed": "Could not resolve the preview",
  "quotas.byProject": "Share of the window by project",
  "quotas.orgLevel": "Organization-level work",
  "quotas.tokensEstimated": "Estimated tokens",
  "quotas.usedShort": "Used",
  "runs.allProjects": "All projects",
  "runs.endOfList": "{n} attempts — end of list.",
  "runs.rerun": "re-run of an earlier attempt",
  "runs.stateFilter": "State",
  "trace.callsTruncated": "Call list shows {n} of {total}; usage totals cover every call.",
  "usage.coverageComplete": "covers all {total} calls",
  "usage.coverageTruncated": "covers {n} of {total} calls",
  "attemptState.running": "Running",
  "attemptState.succeeded": "Succeeded",
  "attemptState.failed": "Failed",
  "attemptState.interrupted": "Interrupted",
  "attemptState.cancelled": "Cancelled",
  "attemptState.superseded": "Superseded",
  "attemptState.waiting_resource": "Waiting for resource",
} as const;
